/*
<summary>
   This class component is all about Managing Defective Items data.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import { IAddDefectiveItem } from '../../models/forms/IAddUpdateDefectiveItem';
import { IReactSelectOption } from '../../models/ICommon';
import { IDefectiveItemFilter } from '../../models/IDefectiveItemFilter';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IDefectiveItemCSVImportResult,
  IDefectiveItemList,
  IDefectiveItemListVM,
} from '../../models/response/IDefectiveItemResponse';
import { formatMessage } from '../../translations/format-message';
import {
  AddEditDefectiveItemState,
  GetAllDefectiveItemState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { downloadCsvFromRows } from './utils/download-csv';

export class DefectiveItemsStore {
  initialStateValue = ObservableInitialState;
  defectiveItemList: IDefectiveItemListVM = GetAllDefectiveItemState;
  getAllDefectiveItemState = { ...this.initialStateValue };
  addDefectiveItemState = { ...this.initialStateValue };
  getSubjectListState = { ...this.initialStateValue };
  deleteDefectiveItemState = { ...this.initialStateValue };
  getDefectiveItemByIdState = { ...this.initialStateValue };
  subjectSuggestions: string[] = [];
  itemSuggestions: string[] = [];
  subjectSuggestionsRequestId = 0;
  fileUploadState = { ...this.initialStateValue };
  importResults: IDefectiveItemCSVImportResult[] = [];
  defectiveItem: IDefectiveItemList | undefined = undefined;

  constructor() {
    makeObservable(this, {
      defectiveItemList: observable,
      getAllDefectiveItemState: observable,
      addDefectiveItemState: observable,
      getSubjectListState: observable,
      subjectSuggestions: observable,
      itemSuggestions: observable,
      fileUploadState: observable,
      importResults: observable,
      getDefectiveItemByIdState: observable,
      defectiveItem: observable,
      deleteDefectiveItemState: observable,
      GetDefectiveItemListService: action,
      AddDefectiveItemService: action,
      GetDefectiveItemSubjectListService: action,
      GetDefectiveItemBySubjectListService: action,
      DefectiveItemFileUploadService: action,
      DeleteDefectiveItemService: action,
      GetDefectiveItemByIdService: action,
      UpdateDefectiveItemService: action,
      resetAddDefectiveItemState: action,
      resetSubjectSuggestions: action,
      resetFileUploadState: action,
      reset: action,
      resetDeleteDefectiveItemState: action,
      resetGetDefectiveItemByIdState: action,
      allDefectiveItems: computed,
      subjectOptions: computed,
      itemOptions: computed,
      defectiveItemDetails: computed,
      resetStore: action,
    });
  }

  //#region Actions
  /**
   * This function is used to get the paginated list of defective items from API.
   * @param tenantId: Tenant identifier
   * @param pageNo: Page number to fetch
   * @param pageSize: Number of records per page
   * @param filter: Subject/Item/Classification/Classification2/Column/Order to filter and sort by
   */
  GetDefectiveItemListService = (
    tenantId: number,
    pageNo: number,
    pageSize: number,
    filter?: IDefectiveItemFilter
  ) => {
    this.getAllDefectiveItemState.inProgress = true;
    const url = URLConstants.GetDefectItemList;
    // The backend computes OFFSET as PageSize * (PageNo - 1); a missing/zero
    // PageNo makes that negative and the query fails, so guard it here too.
    const data = {
      TenantId: tenantId,
      PageNo: pageNo && pageNo > 0 ? pageNo : 1,
      PageSize: pageSize,
      Subject: filter?.Subject || '',
      Item: filter?.Item || '',
      Classification: filter?.Classification || '',
      Classification2: filter?.Classification2 || ''
    };
    return baseService
      .postRequest(url, data)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IDefectiveItemListVM>>) => {
          if (response.data.Error) {
            this.getAllDefectiveItemState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.defectiveItemList = response.data.Data;
            this.getAllDefectiveItemState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllDefectiveItemState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllDefectiveItemState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to add a new defective item via API.
   * @param data: Defective item details to be added
   */
  AddDefectiveItemService = (data: IAddDefectiveItem) => {
    this.addDefectiveItemState.inProgress = true;
    const url = URLConstants.AddDefectiveItem;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addDefectiveItemState.error = response.data.Message;
        } else {
          this.addDefectiveItemState.success = true;
        }
      })
      .catch((err: string) => {
        this.addDefectiveItemState.error = err;
      })
      .finally(
        action(() => {
          this.addDefectiveItemState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to get subject suggestions for the Add form's autocomplete from API.
   * @param tenantId: Tenant identifier
   * @param subject: Partial subject text typed by the user
   */
  // Backs the Subject field's autocomplete suggestions on the Add form.
  GetDefectiveItemSubjectListService = (tenantId: number, subject: string) => {
    this.getSubjectListState.inProgress = true;
    const requestId = ++this.subjectSuggestionsRequestId;
    const isLatestRequest = () => requestId === this.subjectSuggestionsRequestId;
    const url = `${URLConstants.GetDefectiveItemSubjectList}?Subject=${encodeURIComponent(subject)}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (!isLatestRequest()) return;
        if (response.data.Error) {
          this.getSubjectListState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.subjectSuggestions = response.data.Data || [];
        }
      })
      .catch((err: string) => {
        if (!isLatestRequest()) return;
        this.getSubjectListState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          if (isLatestRequest()) {
            this.getSubjectListState.inProgress = false;
          }
        })
      );
  };

  /**
   * This function is used to get the items linked to a given subject from API.
   * @param tenantId: Tenant identifier
   * @param subject: Subject to fetch items for
   */
  GetDefectiveItemBySubjectListService = (
    tenantId: number,
    subject: string
  ) => {
    this.getSubjectListState.inProgress = true;
    const url = `${URLConstants.GetItemsBySubjectList}?Subject=${encodeURIComponent(subject)}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (response.data.Error) {
          this.getSubjectListState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
          return [];
        } else {
          const items = response.data.Data || [];
          this.itemSuggestions = items;
          return items;
        }
      })
      .catch((err: string) => {
        this.getSubjectListState.error = err;
        // toast.error(formatMessage(err));
        return [];
      })
      .finally(
        action(() => {
          this.getSubjectListState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to upload a CSV file for bulk Defective Items import.
   * @param tenantId: Tenant Identifier
   * @param csvFile: CSV File to be uploaded
   */
  DefectiveItemFileUploadService = (tenantId: number, csvFile: File) => {
    this.fileUploadState.inProgress = true;
    const url = `${URLConstants.ImportDefectiveItemsCSV}?TenantId=${tenantId}`;
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    return baseService
      .postMultipart(url, formData, {})
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<IDefectiveItemCSVImportResult[]>
          >
        ) => {
          const results = response.data.Data || [];
          this.importResults = results;
          if (results.length > 0) {
            downloadCsvFromRows(
              [
                formatMessage('row_no'),
                formatMessage('subject'),
                formatMessage('status'),
                formatMessage('message'),
              ],
              results.map((row) => [
                row.RowNo,
                row.Subject || '',
                row.Success
                  ? formatMessage('success')
                  : formatMessage('failed'),
                formatMessage(row.Message),
              ]),
              'DefectiveItemsImportResult'
            );
          }
          if (response.data.Error) {
            this.fileUploadState.error = response.data.Message;
          } else {
            this.fileUploadState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.fileUploadState.error = err;
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to reset fileUploadState and importResults to their initial values.
   */
  resetFileUploadState = () => {
    this.fileUploadState = { ...this.initialStateValue };
    this.importResults = [];
  };
  DeleteDefectiveItemService = (defectiveItemId: number) => {
    this.deleteDefectiveItemState.inProgress = true;
    const url = URLConstants.DeleteDefectiveItem + '/' + defectiveItemId;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteDefectiveItemState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteDefectiveItemState.success = true;
      })
      .catch((err: string) => {
        this.deleteDefectiveItemState.error = err;
      })
      .finally(
        action(() => {
          this.deleteDefectiveItemState.inProgress = false;
        })
      );
  };

  GetDefectiveItemByIdService = (tenantId: number, defectiveItemId: number) => {
    this.getDefectiveItemByIdState.inProgress = true;
    let url = `${URLConstants.GetDefectiveItemById}/${defectiveItemId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IDefectiveItemList>>) => {
          if (response.data.Error) {
            this.getDefectiveItemByIdState.error = response.data.Message;
          } else {
            this.defectiveItem = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.getDefectiveItemByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getDefectiveItemByIdState.inProgress = false;
        })
      );
  };

  UpdateDefectiveItemService = (data: IAddDefectiveItem) => {
    this.addDefectiveItemState.inProgress = true;
    let url = URLConstants.UpdateDefectiveItem;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addDefectiveItemState.error = response.data.Message;
        } else {
          this.addDefectiveItemState.success = true;
        }
      })
      .catch((err: string) => {
        this.addDefectiveItemState.error = err;
      })
      .finally(
        action(() => {
          this.addDefectiveItemState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  /**
   * This computed property is used to map defectiveItemList to a list suitable for the Grid component.
   */
  get allDefectiveItems(): IDefectiveItemList[] {
    const defectiveItemData: IDefectiveItemList[] = [];
    if (
      this.defectiveItemList?.Items &&
      this.defectiveItemList.Items.length > 0
    ) {
      this.defectiveItemList.Items.map((item) => {
        defectiveItemData.push({ ...item });
      });
    }
    return defectiveItemData;
  }

  /**
   * This computed property is used to map subjectSuggestions to react-select options.
   */
  get subjectOptions(): IReactSelectOption[] {
    const options: IReactSelectOption[] = [];
    if (this.subjectSuggestions && this.subjectSuggestions.length > 0) {
      this.subjectSuggestions.map((option) => {
        options.push({
          label: option,
          value: option,
        });
      });
    }
    return options;
  }

  /**
   * This computed property is used to map itemSuggestions to react-select options.
   */
  get itemOptions(): IReactSelectOption[] {
    const options: IReactSelectOption[] = [];
    if (this.itemSuggestions && this.itemSuggestions.length > 0) {
      this.itemSuggestions.map((option) => {
        options.push({
          label: option,
          value: option,
        });
      });
    }
    return options;
  }

  get defectiveItemDetails(): IDefectiveItemList {
    if (this.defectiveItem) {
      return {
        Id: this.defectiveItem.Id,
        Subject: this.defectiveItem.Subject,
        Item: this.defectiveItem.Item,
        Classification1: this.defectiveItem.Classification1,
        Classification2: this.defectiveItem.Classification2,
        Comments: this.defectiveItem.Comments,
      };
    }
    return {
      Id: -1,
      ...AddEditDefectiveItemState,
    };
  }
  //#endregion

  //#region Reset
  /**
   * This function is used to reset defectiveItemList observable to its initial value.
   */
  reset = () => {
    this.defectiveItemList = GetAllDefectiveItemState;
  };

  /**
   * This function is used to reset addDefectiveItemState observable to its initial value.
   */
  resetAddDefectiveItemState = () => {
    this.addDefectiveItemState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset subjectSuggestions and getSubjectListState observables to their initial values.
   */
  resetSubjectSuggestions = () => {
    this.subjectSuggestions = [];
    this.getSubjectListState = { ...this.initialStateValue };
  };

  resetDeleteDefectiveItemState = () => {
    this.deleteDefectiveItemState = { ...this.initialStateValue };
  };

  resetGetDefectiveItemByIdState = () => {
    this.getDefectiveItemByIdState = { ...this.initialStateValue };
    this.defectiveItem = undefined;
  };

  /**
   * This function is used to reset all store observables to their initial values.
   */
  resetStore = () => {
    this.defectiveItemList = GetAllDefectiveItemState;
    this.getAllDefectiveItemState = { ...this.initialStateValue };
    this.addDefectiveItemState = { ...this.initialStateValue };
    this.getSubjectListState = { ...this.initialStateValue };
    this.subjectSuggestions = [];
    this.itemSuggestions = [];
    this.fileUploadState = { ...this.initialStateValue };
    this.importResults = [];
    this.getDefectiveItemByIdState = { ...this.initialStateValue };
    this.defectiveItem = undefined;
  };
  //#endregion
}

export default new DefectiveItemsStore();
